import { HStack, Icon } from "@chakra-ui/react";
import { useCallback, useState, useMemo } from "react";
import { MdRefresh, MdCheckCircle, MdSchedule, MdApps } from "react-icons/md";
import { FiEye, FiAlertTriangle, FiCalendar, FiUser, FiClock } from "react-icons/fi";
import WeekNavigator from "features/MeetingEvents/Components/WeekNavigator";
import { useUserListQuery } from "features/User/hooks";
import FilterButton from "components/Button/FilterButton";
import FilterChipMenu from "components/Button/FilterChipMenu";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import { ActiveFilter, SortOption } from "components/ControlBar/types";
import { TASK_STATUSES } from "features/Resources/Task/constants";
import { STAGE_OPTIONS } from "features/Resources/Shared/constants";
import { TabOption, TabSelector } from "components/TabSelector";

export type ViewMode = "week" | "all";
export type StatusFilterMode = "all" | "active" | "closed" | "upcoming" | "overdue" | "urgent" | "due_soon";
export type SortMode = "due_date_asc" | "due_date_desc" | "effective_start_date_asc" | "effective_start_date_desc";

export interface TasksFilters {
  q?: string;
  assignee_id?: string;
  start_date?: string;
  end_date?: string;
  view_mode?: ViewMode;
  has_issues?: string;
  status_filter?: StatusFilterMode;
  status?: string[];
  stage_key?: string;
  sort?: SortMode;
  audience_filter?: string; // "self", "new-hire", "team"
}

const DEFAULT_TASK_SORT = "effective_start_date_asc";
const DEFAULT_TASK_SORT_OPTIONS: SortOption[] = [
  { label: "Start Date: Oldest First", value: "effective_start_date_asc" },
  { label: "Start Date: Newest First", value: "effective_start_date_desc" },
  { label: "Due Date: Oldest First", value: "due_date_asc" },
  { label: "Due Date: Newest First", value: "due_date_desc" },
];

interface TasksFiltersProps {
  filters: TasksFilters;
  onFiltersChange: (filters: TasksFilters) => void;
  onReset: () => void;
  hideViewMode?: boolean;
  currentSort?: string;
  defaultSortString?: string;
  onSortChange?: (value: string) => void;
  sortOptions?: SortOption[];
  includeNotStarted?: boolean;
  onToggleNotStarted?: () => void;
  showCompleted?: boolean;
  onToggleShowCompleted?: () => void;
  /** When provided, scopes the Assignee filter to these options instead of all company users. */
  assigneeOptions?: { label: string; value: string }[];
}

function FilterTabs({
  options,
  value,
  onChange,
}: {
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <TabSelector value={value} onChange={onChange}>
      {options.map((option) => (
        <TabOption key={option.value} value={option.value}>
          {option.label}
        </TabOption>
      ))}
    </TabSelector>
  );
}

const viewModeOptions = [
  { label: "View All", value: "all" },
  { label: "View by Week", value: "week" },
];

const statusFilterOptions = [
  { label: "Open", value: "active", icon: <Icon as={MdRefresh} boxSize={4} color="gray.600" /> },
  { label: "Closed", value: "closed", icon: <Icon as={MdCheckCircle} boxSize={4} color="gray.600" /> },
  { label: "Not Started", value: "upcoming", icon: <Icon as={MdSchedule} boxSize={4} color="gray.600" /> },
  { label: "All", value: "all", icon: <Icon as={MdApps} boxSize={4} color="gray.600" /> },
];

const individualStatusOptions = Array.from(TASK_STATUSES.values()).map((status) => ({
  label: status.label,
  value: status.value,
  color: status.colors,
}));

export default function TasksFiltersComponent({
  filters,
  onFiltersChange,
  onReset,
  hideViewMode = false,
  currentSort = DEFAULT_TASK_SORT,
  defaultSortString = DEFAULT_TASK_SORT,
  onSortChange: onSortChangeProp,
  sortOptions = DEFAULT_TASK_SORT_OPTIONS,
  includeNotStarted = false,
  onToggleNotStarted,
  showCompleted = false,
  onToggleShowCompleted,
  assigneeOptions,
}: TasksFiltersProps) {
  const [localSort, setLocalSort] = useState(DEFAULT_TASK_SORT);
  const onSortChange = onSortChangeProp || setLocalSort;
  const effectiveSort = onSortChangeProp ? currentSort : localSort;

  const { data: allUsers = [] } = useUserListQuery();

  const allAssigneeOptions = useMemo(
    () =>
      assigneeOptions ??
      allUsers.map((user) => ({
        label: user.fullName,
        value: user.id,
      })),
    [assigneeOptions, allUsers],
  );

  const selectedAssignee = useMemo(
    () => (filters.assignee_id ? allAssigneeOptions.find((o) => o.value === filters.assignee_id) ?? null : null),
    [filters.assignee_id, allAssigneeOptions],
  );

  const selectedStage = useMemo(
    () => (filters.stage_key ? STAGE_OPTIONS.find((o) => o.value === filters.stage_key) ?? null : null),
    [filters.stage_key],
  );

  const selectedStatusFilter = useMemo(
    () => statusFilterOptions.find((o) => o.value === (filters.status_filter || "all")) ?? null,
    [filters.status_filter],
  );

  const handleFilterChange = useCallback(
    (key: keyof TasksFilters, value: string | undefined) => {
      const newValue = value === "" ? undefined : value;
      if (filters[key] !== newValue) {
        onFiltersChange({
          ...filters,
          [key]: newValue,
        });
      }
    },
    [filters, onFiltersChange],
  );

  const handleWeekChange = useCallback(
    (startDate: string, endDate: string) => {
      onFiltersChange({
        ...filters,
        start_date: startDate,
        end_date: endDate,
      });
    },
    [filters, onFiltersChange],
  );


  const handleResetFilters = useCallback(() => {
    onReset();
  }, [onReset]);

  // Build active filters for display
  const activeFilters: ActiveFilter[] = [];

  if (filters.assignee_id) {
    activeFilters.push({
      key: "assignee",
      label: `Assignee: ${selectedAssignee?.label || "Unknown"}`,
      onRemove: () => handleFilterChange("assignee_id", undefined),
    });
  }

  if (filters.status_filter && filters.status_filter !== "all" && filters.status_filter !== undefined) {
    activeFilters.push({
      key: "status_filter",
      label:
        filters.status_filter === "urgent" || filters.status_filter === "overdue"
          ? "Urgent and Overdue"
          : selectedStatusFilter?.label || "Unknown",
      onRemove: () => handleFilterChange("status_filter", "all"),
    });
  }

  if (filters.status && filters.status.length > 0) {
    filters.status.forEach((statusValue) => {
      const statusOption = individualStatusOptions.find((opt) => opt.value === statusValue);
      if (statusOption) {
        activeFilters.push({
          key: `status-${statusValue}`,
          label: `Status: ${statusOption.label}`,
          onRemove: () => {
            const newStatuses = filters.status?.filter((s) => s !== statusValue) || [];
            onFiltersChange({
              ...filters,
              status: newStatuses.length > 0 ? newStatuses : undefined,
            });
          },
        });
      }
    });
  }

  if (filters.stage_key) {
    activeFilters.push({
      key: "stage",
      label: `Stage: ${selectedStage?.label || "Unknown"}`,
      onRemove: () => handleFilterChange("stage_key", undefined),
    });
  }

  if (filters.has_issues) {
    activeFilters.push({
      key: "has_issues",
      label: "With Issues",
      onRemove: () => handleFilterChange("has_issues", undefined),
    });
  }

  if (filters.audience_filter) {
    const audienceLabels: Record<string, string> = {
      self: "My Tasks",
      manager: "Manager Tasks",
      "new-hire": "New Hire Tasks",
      team: "Team Tasks",
    };
    activeFilters.push({
      key: "audience",
      label: audienceLabels[filters.audience_filter] || filters.audience_filter,
      onRemove: () => handleFilterChange("audience_filter", undefined),
    });
  }

  return (
    <>
      {!hideViewMode && (
        <HStack justifyContent="space-between" flexWrap="wrap">
          <FilterTabs
            options={viewModeOptions}
            value={filters.view_mode || "week"}
            onChange={(value) => handleFilterChange("view_mode", value)}
          />

          {filters.view_mode === "week" && (
            <WeekNavigator startTime={filters.start_date} endTime={filters.end_date} onWeekChange={handleWeekChange} />
          )}
        </HStack>
      )}
      <FilterSortSearchBar
        searchPlaceholder="Search by task name or assignee..."
        searchValue={filters.q}
        onSearchChange={(value) => handleFilterChange("q", value)}
        currentSort={effectiveSort}
        defaultSort={defaultSortString}
        onSortChange={onSortChange}
        sortOptions={sortOptions}
        quickFilters={
          <>
            {/* "Show Not Started" surfaces upcoming tasks, mutually exclusive with the active-task
                filters below (Show Completed / Overdue / Due Soon) — those disable while it's on.
                My Tasks is an assignee filter (orthogonal to status), so it stays selectable. */}
            <FilterButton
              onClick={() => handleFilterChange("audience_filter", filters.audience_filter === "self" ? undefined : "self")}
              isActive={filters.audience_filter === "self"}
              icon={FiUser}
              size="sm"
            >
              My Tasks
            </FilterButton>
            {onToggleShowCompleted && (
              <FilterButton
                onClick={onToggleShowCompleted}
                isActive={showCompleted}
                isDisabled={includeNotStarted}
                icon={FiEye}
                size="sm"
              >
                Show Completed
              </FilterButton>
            )}
            <FilterButton
              onClick={() => handleFilterChange("status_filter", filters.status_filter === "overdue" ? undefined : "overdue")}
              isActive={filters.status_filter === "overdue"}
              isDisabled={includeNotStarted}
              icon={FiAlertTriangle}
              size="sm"
            >
              Overdue
            </FilterButton>
            <FilterButton
              onClick={() => handleFilterChange("status_filter", filters.status_filter === "due_soon" ? undefined : "due_soon")}
              isActive={filters.status_filter === "due_soon"}
              isDisabled={includeNotStarted}
              icon={FiCalendar}
              size="sm"
            >
              Due Soon
            </FilterButton>
            {onToggleNotStarted && (
              <FilterButton
                onClick={onToggleNotStarted}
                isActive={includeNotStarted}
                icon={FiClock}
                size="sm"
              >
                Show Not Started
              </FilterButton>
            )}
            <FilterChipMenu
              label="Assignee"
              options={allAssigneeOptions}
              value={filters.assignee_id}
              onChange={(next) => handleFilterChange("assignee_id", next)}
              searchable
            />
            <FilterChipMenu
              label="Stage"
              options={STAGE_OPTIONS}
              value={filters.stage_key}
              onChange={(next) => handleFilterChange("stage_key", next)}
            />
          </>
        }
        activeFilters={[]}
        onReset={handleResetFilters}
        hasActiveFilters={false}
      />
    </>
  );
}
